import { DayOfWeek, Shift } from '@api'
import { addDays } from 'date-fns/addDays'
import { addYears } from 'date-fns/addYears'
import { format } from 'date-fns/format'
import { startOfWeek } from 'date-fns/startOfWeek'
import { subYears } from 'date-fns/subYears'

import { getLocalDateTime } from '@/utils/date.ts'

import { weekdays } from '../tasks/service/repeatable-task-service'
import type { NormalizedShiftMutationOptions } from './types'
import {
  nonRepeatOption,
  repeatDaily,
  repeatWeekly,
} from './ui/create-edit-form/consts'
import type { ShiftFormValues } from './ui/create-edit-form/types'

export const getWeekDays = (date: Date) => {
  const days = [startOfWeek(date, { weekStartsOn: 1 })]

  while (days.length < 7) {
    days.push(addDays(days[days.length - 1], 1))
  }

  return days
}

export const uppercase = <T extends string>(string: T): Uppercase<T> =>
  string.toUpperCase() as Uppercase<T>

export const normalizeShiftMutationData = (
  values: ShiftFormValues,
): NormalizedShiftMutationOptions => {
  let options: NormalizedShiftMutationOptions = {
    isRepeatable: false,
    data: {
      startedAt: values.startedAt.toISOString(),
      finishedAt: values.finishedAt.toISOString(),
      memberIds: values.memberIds.map(Number),
      ...(values.creatorIds
        ? { creatorIds: values.creatorIds.map(Number) }
        : { creatorIds: [Number(values.creatorId)] }),
    },
  }

  if (values.recurring !== nonRepeatOption.value) {
    let days: DayOfWeek[] = []

    if (values.recurring === repeatWeekly.value && values.days) {
      days = values.days.map(uppercase) as DayOfWeek[]
    } else if (values.recurring === repeatDaily.value) {
      days = getWeekDays(values.startedAt).map((date) =>
        uppercase(weekdays[date.getDay()]),
      ) as DayOfWeek[]
    }

    options = {
      isRepeatable: true,
      data: {
        ...options.data,
        days,
        repeatedUntil: values.endDate
          ? format(values.endDate, 'yyyy-MM-dd')
          : null,
        view: {
          start: format(subYears(values.startedAt, 1), 'yyyy-MM-dd'),
          end: format(addYears(values.finishedAt, 1), 'yyyy-MM-dd'),
        },
      },
    }
  }

  return options
}

export const convertSecondsToMinutes = (
  seconds: number,
): `${number}m ${number}s` => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds - mins * 60)

  return `${mins}m ${secs}s`
}

export const getRepetitionDays = (shifts?: Shift[], repeatableId?: number) =>
  repeatableId && shifts
    ? [
        ...new Set(
          shifts.reduce<(typeof weekdays)[number][]>(
            (acc, { startedAt, repeatableShiftId }) => {
              if (repeatableId === repeatableShiftId) {
                acc.push(weekdays[new Date(startedAt).getDay()])
              }

              return acc
            },
            [],
          ),
        ).values(),
      ]
    : []

export const getRepetitionValue = (
  repeatDays: (typeof weekdays)[number][],
): string => {
  if (!repeatDays.length) {
    return nonRepeatOption.value
  }
  if (repeatDays.length === weekdays.length) {
    return repeatDaily.value
  }

  return repeatWeekly.value
}

export function addOneDay(date: Date) {
  const newDate = new Date(date)

  newDate.setDate(newDate.getDate() + 1)

  return newDate
}

export const highlightColumn = (
  table: HTMLTableElement,
  columnIndex: number,
) => {
  const tableHead = table.querySelector('thead')
  const tableBody = table.querySelector('tbody')

  const tableHeadCell = tableHead?.querySelector(
    `th:nth-child(${columnIndex + 1})`,
  )
  const tableBodyCells = tableBody?.querySelectorAll(
    `td:nth-child(${columnIndex + 1})`,
  )

  if (tableHeadCell && tableBodyCells) {
    const { left, top, width, height } = tableHeadCell.getBoundingClientRect()

    const div = document.createElement('div')

    div.classList.add('absolute', 'z-10', 'bg-primary-500', 'rounded')

    div.style.left = `${left}px`
    div.style.top = `${top}px`
    div.style.width = `${width}px`
    div.style.height = `${height}px`

    tableHead?.appendChild(div)

    setTimeout(() => {
      div.style.left = '50%'
      div.style.top = '50%'
      div.style.width = '0px'
      div.style.height = '0px'
    }, 0)

    setTimeout(() => {
      tableHead?.removeChild(div)
    }, 1000)

    const headerCellClassNames = [
      'bg-primary-500/25',
      'border-x',
      'border-x-border',
    ]
    const bodyCellClassNames = ['bg-primary-500/25']

    tableHeadCell.classList.add(...headerCellClassNames)
    setTimeout(() => {
      tableHeadCell.classList.remove(...headerCellClassNames)
    }, 1000)

    tableBodyCells.forEach((cell) => {
      cell.classList.add(...bodyCellClassNames)
      setTimeout(() => {
        cell.classList.remove(...bodyCellClassNames)
      }, 1000)
    })
  }
}

export const highlightRow = (table: HTMLTableElement, rowId: string) => {
  const row = table.querySelector(`tr[data-id="${rowId}"]`)

  if (!row) return

  const rowClassNames = ['bg-primary-500/25']

  row.scrollIntoView({ block: 'center' })

  row.classList.add(...rowClassNames)
  setTimeout(() => {
    row.classList.remove(...rowClassNames)
  }, 1000)
}

export const removeSearchParamFromUrl = (param: string) => {
  const url = new URL(window.location.href)

  url.searchParams.delete(param)
  window.history.replaceState({}, document.title, url.pathname + url.search)
}

export const formatLocally = (...args: Parameters<typeof format>) => {
  const [date, ...rest] = args

  return format(getLocalDateTime(date), ...rest)
}

export const getDayFromMonday = (date: Date) => {
  const day = date.getDay()

  return day === 0 ? 6 : day - 1
}

export const getFirstDayOfWeek = (date: Date) => {
  const day = getDayFromMonday(date)
  const firstDayOfWeek = addDays(date, -day)

  firstDayOfWeek.setHours(0, 0, 0, 0)

  return firstDayOfWeek
}

export const getShiftDuration = (shift: Shift) => {
  const startedAt = new Date(shift.startedAt)
  const finishedAt = new Date(shift.finishedAt)
  const duration = finishedAt.getTime() - startedAt.getTime()

  return duration / 1000
}

export const convertSecondsToHoursOrMinutes = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hours * 3600) / 60)

  return [hours ? `${hours}h` : '', minutes ? `${minutes}m` : ''].join(' ')
}
